import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Radio, Copy, Eye, EyeOff, KeyRound, Webhook, Send,
  Link2, ShieldCheck, AlertTriangle, RefreshCw } from "lucide-react";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { GlassCard } from "@/components/GlassCard";
import { StatusBadge } from "@/components/Badges";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { goldBtn, goldBtnSm, glassBtn, inputCls, labelCls } from "@/lib/constants";

function Row({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className={labelCls}>{label}</span>
      {children}
    </div>
  );
}

function CopyField({ value, testid }) {
  const copy = () => { navigator.clipboard.writeText(value); toast.success("Copied to clipboard"); };
  return (
    <div className="flex items-stretch gap-2">
      <code data-testid={testid}
        className="flex-1 pdg-mono text-xs sm:text-sm text-foreground/90 break-all bg-[hsl(var(--pdg-obsidian))] rounded-lg px-3 py-2.5 border border-border">
        {value}
      </code>
      <button onClick={copy} data-testid={`${testid}-copy`} title="Copy"
        className="shrink-0 w-11 rounded-lg border border-border text-foreground/70 hover:text-[hsl(var(--pdg-gold))] hover:border-[hsl(var(--pdg-gold)/0.4)] transition-colors flex items-center justify-center">
        <Copy size={15} />
      </button>
    </div>
  );
}

export default function AdminQuantus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [form, setForm] = useState({ outbound_url: "", outbound_token: "" });
  const [saving, setSaving] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [ping, setPing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/quantus/integration");
      setData(data);
      setForm((f) => ({ ...f, outbound_url: data.outbound_configured ? data.outbound_url : "" }));
    } catch (e) {
      toast.error("Could not load Quantus integration");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reveal = async () => {
    if (revealed) { setRevealed(null); return; }
    setRevealing(true);
    try {
      const { data } = await api.post("/admin/quantus/reveal-secret");
      setRevealed(data.value);
      toast.info("Secret revealed. Handle with care \u2014 this action is audit-logged.");
    } catch (e) { toast.error("Could not reveal secret"); }
    finally { setRevealing(false); }
  };

  const copySecret = () => {
    if (!revealed) { toast.error("Reveal the secret first to copy it"); return; }
    navigator.clipboard.writeText(revealed);
    toast.success("Webhook secret copied to clipboard");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { outbound_url: form.outbound_url.trim() };
      if (form.outbound_token.trim()) payload.outbound_token = form.outbound_token.trim();
      await api.put("/admin/quantus/integration", payload);
      toast.success("Outbound endpoint saved.");
      setForm((f) => ({ ...f, outbound_token: "" }));
      setPing(null);
      await load();
    } catch (e) { toast.error("Could not save endpoint"); }
    finally { setSaving(false); }
  };

  const sendPing = async () => {
    setPinging(true);
    setPing(null);
    try {
      const { data } = await api.post("/admin/quantus/test-ping");
      setPing(data);
      if (data.delivered) toast.success("Handshake delivered \u2014 outbound connection is alive.");
      else if (data.reachable) toast.warning(`Endpoint reachable but returned HTTP ${data.http_status}.`);
      else toast.error("Endpoint unreachable.");
    } catch (e) { toast.error("Test ping failed"); }
    finally { setPinging(false); }
  };

  if (loading) {
    return (
      <PortalLayout title="Quantus Integration">
        <div className="grid gap-6 lg:grid-cols-2">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-48 rounded-2xl bg-[hsl(var(--pdg-elevated)/0.4)]" />)}
        </div>
      </PortalLayout>
    );
  }

  const connected = data?.connected;
  const configured = data?.outbound_configured;

  return (
    <PortalLayout title="Quantus Integration">
      <p className="text-sm text-muted-foreground mb-6 max-w-3xl">
        Connect Prime Deal Gateway with Quantus Pulse. Register this platform as a trusted deal
        source using the inbound URL and webhook secret below, then point the outbound endpoint at
        your live Quantus Pulse inbound API and confirm the link with a test ping.
      </p>

      {/* Connection status */}
      <GlassCard className="p-6 mb-6" data-testid="quantus-connection-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--pdg-gold)/0.12)] flex items-center justify-center">
              <Radio size={19} className="text-[hsl(var(--pdg-gold))]" strokeWidth={1.7} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground">Inbound connection</span>
                <StatusBadge
                  status={connected ? "success" : "attention"}
                  label={connected ? "Connected" : "No signed webhook in 30 days"}
                  className="ml-1" />
              </div>
              <div className="text-xs text-muted-foreground mt-1" data-testid="quantus-connection-detail">
                {connected
                  ? `${data.valid_webhooks_30d} valid signed webhook${data.valid_webhooks_30d === 1 ? "" : "s"} in the last 30 days`
                  : "Awaiting a valid signed webhook from Quantus Pulse"}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Last valid webhook</div>
            <div className="text-sm text-foreground/90" data-testid="quantus-last-valid">
              {data.last_valid_webhook_at ? new Date(data.last_valid_webhook_at).toLocaleString() : "\u2014"}
            </div>
          </div>
        </div>
      </GlassCard>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Inbound webhook */}
        <GlassCard className="p-6" data-testid="quantus-inbound-card">
          <div className="flex items-center gap-3 mb-5">
            <Webhook size={18} className="text-[hsl(var(--pdg-gold))]" strokeWidth={1.7} />
            <h2 className="pdg-serif text-xl text-foreground">Inbound webhook</h2>
          </div>
          <div className="space-y-4">
            <Row label="This platform's inbound webhook URL">
              <CopyField value={data.inbound_webhook_url} testid="quantus-inbound-url" />
            </Row>
            <div className="flex items-start gap-2 text-xs text-muted-foreground leading-6">
              <ShieldCheck size={14} className="text-[hsl(var(--pdg-gold))] mt-0.5 shrink-0" />
              <span>Quantus signs each request with {data.signature_scheme}. Requests failing verification, replay, or idempotency checks are rejected and logged.</span>
            </div>
          </div>
        </GlassCard>

        {/* Webhook secret */}
        <GlassCard className="p-6" data-testid="quantus-secret-card">
          <div className="flex items-center gap-3 mb-5">
            <KeyRound size={18} className="text-[hsl(var(--pdg-gold))]" strokeWidth={1.7} />
            <h2 className="pdg-serif text-xl text-foreground">Webhook secret</h2>
          </div>
          <div className="space-y-4">
            <Row label={data.secret_name}>
              <div className="flex items-stretch gap-2">
                <code data-testid="quantus-secret-value"
                  className="flex-1 pdg-mono text-xs sm:text-sm text-foreground/90 break-all bg-[hsl(var(--pdg-obsidian))] rounded-lg px-3 py-2.5 border border-border">
                  {revealed || data.secret_masked}
                </code>
                <button onClick={reveal} disabled={revealing} data-testid="quantus-secret-reveal" title={revealed ? "Hide" : "Reveal"}
                  className="shrink-0 w-11 rounded-lg border border-border text-foreground/70 hover:text-[hsl(var(--pdg-gold))] hover:border-[hsl(var(--pdg-gold)/0.4)] transition-colors flex items-center justify-center disabled:opacity-50">
                  {revealed ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
                <button onClick={copySecret} data-testid="quantus-secret-copy" title="Copy secret"
                  className="shrink-0 w-11 rounded-lg border border-border text-foreground/70 hover:text-[hsl(var(--pdg-gold))] hover:border-[hsl(var(--pdg-gold)/0.4)] transition-colors flex items-center justify-center">
                  <Copy size={15} />
                </button>
              </div>
            </Row>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>Fingerprint <span className="pdg-mono text-[hsl(var(--pdg-gold))]" data-testid="quantus-secret-fingerprint">{data.secret_fingerprint}</span></span>
              <span>Length {data.secret_length}</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-muted-foreground leading-6">
              <AlertTriangle size={14} className="text-[hsl(38_45%_60%)] mt-0.5 shrink-0" />
              <span>Masked by default. Revealing the raw secret is admin-only and recorded in the audit trail. Paste it into Quantus Pulse to authorise this source.</span>
            </div>
          </div>
        </GlassCard>

        {/* Outbound endpoint */}
        <GlassCard className="p-6 lg:col-span-2" data-testid="quantus-outbound-card">
          <div className="flex items-center gap-3 mb-5">
            <Link2 size={18} className="text-[hsl(var(--pdg-gold))]" strokeWidth={1.7} />
            <h2 className="pdg-serif text-xl text-foreground">Outbound endpoint</h2>
            <StatusBadge status={configured ? "success" : "attention"}
              label={configured ? "Configured" : "Not configured"} />
          </div>

          {!configured && (
            <div className="flex items-start gap-2 text-xs text-[hsl(38_45%_70%)] bg-[hsl(38_22%_12%)] border border-[hsl(38_22%_26%)] rounded-lg px-3 py-2.5 mb-5 leading-6" data-testid="quantus-outbound-warning">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>Currently pointing at a placeholder. Enter your live Quantus Pulse inbound endpoint to enable outbound deal pushes.</span>
            </div>
          )}

          <div className="grid gap-5 md:grid-cols-2">
            <Row label="Quantus Pulse inbound endpoint (URL)">
              <input value={form.outbound_url} onChange={(e) => setForm({ ...form, outbound_url: e.target.value })}
                data-testid="quantus-outbound-url-input" className={inputCls}
                placeholder="https://<project-ref>.supabase.co/functions/v1/inbound-deals" />
            </Row>
            <Row label={"Auth token (optional \u2014 Supabase key)"}>
              <input type="password" value={form.outbound_token} onChange={(e) => setForm({ ...form, outbound_token: e.target.value })}
                data-testid="quantus-outbound-token-input" className={inputCls}
                placeholder={data.outbound_auth_configured ? "\u2022\u2022\u2022\u2022 configured \u2014 type to replace" : "Sent as Authorization: Bearer + apikey"} />
            </Row>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-6">
            <button onClick={save} disabled={saving} data-testid="quantus-outbound-save" className={goldBtn}>
              {saving ? <RefreshCw size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
              {saving ? "Saving\u2026" : "Save endpoint"}
            </button>
            <button onClick={sendPing} disabled={pinging} data-testid="quantus-send-test-ping" className={glassBtn}>
              <Send size={15} /> {pinging ? "Pinging\u2026" : "Send Test Ping"}
            </button>
            <span className="text-xs text-muted-foreground">Outbound requests are signed with the PDG secret ({data.signature_scheme.split(" (")[0]}).</span>
          </div>

          {ping && (
            <div className="mt-5 rounded-xl border border-border bg-[hsl(var(--pdg-obsidian))] p-4" data-testid="quantus-ping-result">
              <div className="flex items-center gap-3 mb-2">
                <StatusBadge
                  status={ping.delivered ? "delivered" : ping.reachable ? "attention" : "error"}
                  label={ping.delivered ? "Delivered" : ping.reachable ? "Reachable" : "Unreachable"} />
                {ping.http_status != null && <span className="text-xs text-muted-foreground">HTTP {ping.http_status}</span>}
                {ping.auth_header && <span className="text-xs text-muted-foreground">auth header sent</span>}
              </div>
              <div className="text-xs text-muted-foreground pdg-mono break-all">Target: {ping.target}</div>
              {ping.detail && <div className="text-xs text-muted-foreground pdg-mono break-all mt-1">Response: {ping.detail}</div>}
            </div>
          )}
        </GlassCard>
      </div>
    </PortalLayout>
  );
}
